import type { SchedulePublishGate } from "../lib/schedule-publish-gate";

/**
 * The shared publish review gate (lock O-4: one review gate, not one per surface).
 *
 * Rendered by the connected wizard's Review step and by the Builder's publish dialog, from
 * the same host review and the same resolved gate, so the two surfaces cannot disagree about
 * what a week creates or why it cannot run.
 *
 * Presentational by construction: no client, no RPC, no state. It renders what
 * `resolveSchedulePublishGate` proved and nothing else, which is why both callers can be
 * tested against the same expectations.
 *
 * The primary control renders ONLY when the gate says publish can genuinely run. A blue
 * control that cannot run is the defect this component exists to prevent, so there is no
 * prop that can force one.
 */
export function SchedulePublishReview({
  gate,
  weekLine,
  publishLabel = "Publish for officers",
  busy = false,
  onPublish,
  onRepair,
  facts = [],
  children,
}: {
  readonly gate: SchedulePublishGate;
  /** The week under review, already formatted by the caller in its own house style. */
  readonly weekLine: string;
  readonly publishLabel?: string;
  readonly busy?: boolean;
  readonly onPublish: () => void;
  /** Takes the operator to the step that repairs the block, where one exists. */
  readonly onRepair?: (step: NonNullable<SchedulePublishGate["block"]>["repairStep"]) => void;
  /** Caller-owned pattern facts (hours, days, where). Never counts: counts come from the gate. */
  readonly facts?: readonly string[];
  /** Per-day detail the caller can prove, e.g. the Builder's row table. */
  readonly children?: React.ReactNode;
}) {
  const block = gate.block;
  const blockerId = "schedpub-blocker";
  return (
    <div className="schedpub">
      <p className="schedpub__week">{weekLine}</p>
      {/*
        The count line is the authoritative pre-publish claim, so it is a live region: an
        operator who changes the week or the pattern hears the new number rather than acting
        on the one they read a moment ago.
      */}
      <p className="schedpub__counts" role="status">{gate.countLine}</p>
      {facts.length > 0 ? (
        <ul className="schedpub__facts">
          {facts.map((fact) => (
            <li key={fact}>{fact}</li>
          ))}
        </ul>
      ) : null}
      {gate.warningLines.length > 0 ? (
        <ul className="schedpub__warnings" aria-label="Before you publish">
          {gate.warningLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
      {children}
      {/*
        D-02 permanence when seats would be created. The older "nothing on the calendar" line
        contradicted that seats become part of the service record, so it only remains when
        this review creates no seats (already published or nothing to create).
      */}
      <p className="schedpub__footnote">
        {gate.consequenceLine ?? "Nothing is on the calendar until you publish."}
      </p>
      <div className="schedpub__actions">
        {gate.canPublish ? (
          <button
            type="button"
            className="minter__confirm-go"
            onClick={onPublish}
            disabled={busy}
          >
            {publishLabel}
          </button>
        ) : null}
        {block !== null && block.repairLabel !== null && onRepair !== undefined ? (
          <button
            type="button"
            className="builder__navbtn"
            onClick={() => onRepair(block.repairStep)}
          >
            {block.repairLabel}
          </button>
        ) : null}
      </div>
      {/*
        Exactly one reason, and it is announced. Stacked reasons make the operator guess which
        one to fix first, which the flow lock refuses.
      */}
      {block !== null ? (
        <p className="schedpub__blocker" id={blockerId} role="status">
          {block.reason}
        </p>
      ) : null}
    </div>
  );
}
